import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

const getNoteUrl = (tenantId: number, relatedEntityId?: number) => {
  if (!tenantId) throw new Error("Tenant ID is required.");
  let url = `${API_BASE_URL}/note/?tenant_id=${tenantId}`;
  if (relatedEntityId) {
    url += `&related_entity_id=${relatedEntityId}`;
  }
  return url;
};

const addTenantToNote = <T extends { tenant_id?: number | null }>(
  note: Partial<T>,
  tenantId: number
): Partial<T> => {
  if (!tenantId) {
    throw new Error("Tenant ID is required.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...note,
    tenant_id: tenantId,
    created_by: currentUserId,
  };
};

export interface Note {
  note_id?: number;
  title: string;
  description: string;
  created_by:
    | {
        user_id: number;
        full_name: string;
        avatar_url: string;
      }
    | number;
  created_datetime?: string;
  related_entity_name: string;
  related_entity_id: number;
  tenant_id: number;
}

export interface NoteRequest {
  title: string;
  description: string;
  created_by: number;
  related_entity_id: number;
  related_entity_name: string;
  tenant_id: number;
  attachment_urls?: File[];
}

export interface PaginatedNoteResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Note[];
}

export interface NoteAttachment {
  note_attachment_id: number;
  title: string;
  size_in_kb: number;
  attachment_url: string;
  created_datetime: string;
  note_id: number;
  tenant_id: number;
  created_by: number;
}

export interface NoteComment {
  note_comment_id?: number;
  content: string;
  created_datetime?: string;
  note_id: number;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string | null;
  };
  tenant_id: number;
}

class NoteServiceClass {
  async getNotes(
    tenantId: number,
    relatedEntityId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedNoteResponse> {
    try {
      const url = getNoteUrl(tenantId, relatedEntityId);
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        params: {
          page: page,
          limit: limit,
        },
      });

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch notes");
    }
  }

  async getNoteById(id: number, tenantId: number): Promise<Note> {
    try {
      const response = await axios.get(`${API_BASE_URL}/note/${id}/`, {
        headers: getAuthHeaders(),
        params: {
          tenant_id: tenantId,
        },
      });

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to fetch note");
    }
  }

  async createNote(
    noteData: Partial<NoteRequest>,
    tenantId: number,
    attachments?: File[]
  ): Promise<Note> {
    try {
      const noteWithTenant = addTenantToNote(noteData, tenantId);

      const { attachment_urls, ...cleanNoteData } = noteWithTenant;

      const response = await axios.post(
        `${API_BASE_URL}/note/`,
        cleanNoteData,
        {
          headers: getAuthHeaders(),
        }
      );

      const createdNote = response.data;

      if (attachments && attachments.length > 0 && createdNote.note_id) {
        await this.createNoteAttachments(
          createdNote.note_id,
          tenantId,
          attachments
        );
      }

      return createdNote;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to create note");
    }
  }

  async updateNote(
    id: number,
    noteData: Partial<NoteRequest>,
    tenantId: number,
    attachments?: File[]
  ): Promise<Note> {
    try {
      const authResponse = localStorage.getItem("authResponse");
      let currentUserId = null;

      if (authResponse) {
        try {
          const auth = JSON.parse(authResponse);
          currentUserId = auth.user_id;
        } catch (e) {
          console.warn("Could not parse auth response for user_id");
        }
      }

      const updatePayload = {
        title: noteData.title,
        description: noteData.description,
        tenant_id: tenantId,
        updated_by: currentUserId,
      };

      const response = await axios.patch(
        `${API_BASE_URL}/note/${id}/`,
        updatePayload,
        {
          headers: getAuthHeaders(),
          params: {
            tenant_id: tenantId,
          },
        }
      );

      const updatedNote = response.data;

      if (attachments && attachments.length > 0) {
        await this.createNoteAttachments(id, tenantId, attachments);
      }

      return updatedNote;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to update note");
    }
  }

  async deleteNote(id: number, tenantId: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/note/${id}/`, {
        headers: getAuthHeaders(),
        params: {
          tenant_id: tenantId,
        },
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(error.response?.data?.message || "Failed to delete note");
    }
  }

  async getNoteAttachments(
    tenantId: number,
    noteId: number
  ): Promise<NoteAttachment[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/note-attachment/?tenant_id=${tenantId}&note_id=${noteId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch note attachments"
      );
    }
  }

  async getNoteComments(noteId: number): Promise<NoteComment[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/note-comment/?note_id=${noteId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.results || response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch note comments"
      );
    }
  }

  async createNoteComment(
    commentData: Partial<NoteComment>
  ): Promise<NoteComment> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/note-comment/`,
        commentData,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create note comment"
      );
    }
  }

  async deleteNoteComment(id: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/note-comment/${id}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to delete note comment"
      );
    }
  }

  async deleteNoteAttachment(attachmentId: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/note-attachment/${attachmentId}/`, {
        headers: getAuthHeaders(),
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to delete note attachment"
      );
    }
  }

  async createNoteAttachment(
    noteId: number,
    tenantId: number,
    file: File
  ): Promise<NoteAttachment> {
    try {
      const formData = new FormData();
      formData.append("note_id", String(noteId));
      formData.append("tenant_id", String(tenantId));
      formData.append("attachement_url", file);

      const response = await axios.post(
        `${API_BASE_URL}/note-attachment/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
          params: {
            tenant_id: tenantId,
            note_id: noteId,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create note attachment"
      );
    }
  }

  async createNoteAttachments(
    noteId: number,
    tenantId: number,
    files: File[]
  ): Promise<NoteAttachment[]> {
    try {
      const attachmentPromises = files.map((file) =>
        this.createNoteAttachment(noteId, tenantId, file)
      );

      const attachments = await Promise.all(attachmentPromises);
      return attachments;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create note attachments"
      );
    }
  }
}

export const NoteService = new NoteServiceClass();
